import { Button, IconButton, InputBase } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";
import { ArrowDownward, ArrowUpward } from "@material-ui/icons";
import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import ListSkeleton from "../Skeleton/ListSkeleton";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: "98%",
    background: "#F7F7F7",
  },
  table: {
    width: "98%",
    maxHeight: "70vh",
    [theme.breakpoints.down("sm")]: {
      maxHeight: "60vh",
    },
  },
  search: {
    position: "relative",
    paddingLeft: "5px",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: "rgba(0,0,0,.1)",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,.2)",
    },
    marginBottom: "20px",
    width: "20%",
    "&.Mui-focused": {
      width: "30%",
      transition: "width 0.3s",
    },
    transition: "width 0.3s",
    [theme.breakpoints.down("sm")]: {
      width: "50%",
      "&.Mui-focused": {
        width: "100%",
        transition: "width 0.3s",
      },
    },
  },
  hint: {
    position: "relative",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    marginTop: "auto",
    marginBottom: "auto",
    marginLeft: "auto",
    marginRight: "auto",
    fontSize: "32px",
    textAlign: "center",
    color: "rgba(0,0,0,.6)",
    [theme.breakpoints.down("sm")]: {
      position: "unset",
    },
  },
}));

/**
 * @template {object} T
 * @typedef {TableRowProps<T> & { customRowProps?: { onClick?: (row: T) => void } }} SortableTableRowProps
 */

/**
 * @template {object} T
 * @typedef {object} SortableTableProps
 * @property {TableColumn[]} columns
 * @property {T[]} [tableData]
 * @property {import("react").ComponentType<SortableTableRowProps<T>>} rowComponent
 * @property {boolean} [loading]
 * @property {boolean} [error]
 * @property {() => void} [onRetry]
 * @property {boolean} [hideSearch]
 * @property {number} [defaultSortIndex]
 * @property {(data: T[]) => void} [setTableData]
 * @property {boolean} [reverse]
 * @property {import("../../Types/Pagination").TablePaginationSettings} [pagination]
 * @property {number} [totalItemCount]
 * @property {{ onClick?: (row: NoInfer<T>) => void }} [customRowProps]
 */

/**
 * @template {object} T
 * @param {SortableTableProps<T>} props
 */
export default function SortableTable({
  columns,
  tableData = [],
  rowComponent,
  loading,
  error = false,
  onRetry,
  hideSearch,
  defaultSortIndex,
  setTableData,
  reverse,
  pagination,
  totalItemCount,
  customRowProps = undefined,
}) {
  const [data, setData] = useState(tableData);
  const [filteredData, setFilteredData] = useState(tableData);
  const [sortedProperty, setSortedProperty] = useState(columns[defaultSortIndex ?? 0].propName);
  const [ascending, setAscending] = useState(!reverse);
  const [page, setPage] = useState(0);
  const [, setRowsPerPage] = useState(10);
  const classes = useStyles();

  const RowComponent = rowComponent;

  const handleChangePage = (event, newPage) => {
    pagination?.setCurrentPage(newPage);
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    pagination?.setPageSize(parseInt(event.target.value, 10));
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function sortTable(column) {
    if (column.propName === sortedProperty) {
      pagination?.setSortAscending(!ascending);
      setAscending(!ascending);
    } else {
      pagination?.setSortAscending(false);
      setSortedProperty(column.propName);
      setAscending(false);
    }
  }

  function handleChange(event) {
    if (pagination?.enabled) {
      setPage(0);
      pagination.setSearchValue(event.target.value.toLowerCase());
    } else {
      const value = event.target.value.toLowerCase();
      let result = [];
      result = data.filter((o) =>
        Object.keys(o).some((k) => {
          for (const i in columns) {
            const elem = _.get(o, columns[i].propName);
            if (elem?.toLowerCase().includes(value)) {
              return o[k];
            }
          }
          return null;
        }),
      );
      setFilteredData(result);
    }
  }

  const sortData = useCallback(() => {
    if (pagination?.enabled) {
      pagination.setSortProperty(sortedProperty);
      setFilteredData(data);
      return;
    }

    const arrayCopy = data ? [...data] : [];

    if (arrayCopy.length === 0) {
      setFilteredData(arrayCopy);
      return;
    }

    arrayCopy.sort((a, b) => {
      let aValue = _.get(a, sortedProperty);
      let bValue = _.get(b, sortedProperty);

      const aDate = Date.parse(aValue);
      const bDate = Date.parse(bValue);
      const isDate = !isNaN(aDate) && !isNaN(bDate);

      if (isDate) {
        return ascending ? aDate - bDate : bDate - aDate;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      return ascending ? (aValue > bValue ? 1 : -1) : aValue < bValue ? 1 : -1;
    });

    setFilteredData(arrayCopy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ascending, pagination?.enabled, JSON.stringify(data), sortedProperty]);

  useEffect(() => {
    setData(tableData);
    sortData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tableData), sortData]);

  return (
    <>
      {!hideSearch ? (
        <InputBase className={classes.search} placeholder="Search..." onChange={handleChange} />
      ) : null}

      <TableContainer
        className={classes.table}
        component={Paper}
        style={{ padding: filteredData.length !== 0 ? undefined : "20px 0" }}
      >
        {error ? (
          <div className={classes.hint} data-test="table-error">
            <div style={{ fontSize: 20 }}>We couldn't load this data. Please try again.</div>
            {onRetry && (
              <Button
                variant="contained"
                color="primary"
                onClick={onRetry}
                data-test="table-retry"
                style={{ marginTop: 16 }}
              >
                Retry
              </Button>
            )}
          </div>
        ) : loading ? (
          <ListSkeleton numRows={pagination !== undefined ? pagination.pageSize : 10} />
        ) : filteredData.length !== 0 ? (
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell
                    align="left"
                    onClick={() => sortTable(column)}
                    style={{ fontWeight: "bold" }}
                    key={`columnheader-${index}`}
                  >
                    {column.label}
                    <IconButton>
                      {sortedProperty === column.propName && ascending === true ? (
                        <ArrowUpward />
                      ) : (
                        <ArrowDownward />
                      )}
                    </IconButton>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((elem, index) => (
                <RowComponent
                  columns={columns}
                  element={elem}
                  key={index}
                  index={index}
                  setTableData={setTableData}
                  customRowProps={customRowProps}
                />
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className={classes.hint}>No Results</div>
        )}
      </TableContainer>
      {pagination !== undefined && (
        <TablePagination
          rowsPerPageOptions={pagination.pageSizeOptions}
          component="div"
          count={totalItemCount ?? tableData.length}
          rowsPerPage={pagination.pageSize}
          page={!totalItemCount || totalItemCount <= 0 ? 0 : page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          data-test="tablePagination"
        />
      )}
    </>
  );
}
